import { useState } from 'react';
import { apiJson } from '../api/client';
import { mutateGallery } from '../hooks/galleryCache';
import { NewAssetModal } from './NewAssetModal';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const ENDPOINT = '/api/tools/images';

interface CreateResult {
  id: string;
  r2_key?: string;
  signedUrl?: string;
  [k: string]: unknown;
}

export function NewImageDialog({ open, onClose, onCreated }: Props) {
  const [prompt, setPrompt] = useState('');
  const [aspectRatio, setAspectRatio] = useState('1:1');
  const [resolution, setResolution] = useState('2K');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (): Promise<void> => {
    const p = prompt.trim();
    if (!p) return;
    setBusy(true);
    setErr(null);
    try {
      const res = await apiJson<CreateResult>('POST', ENDPOINT, {
        prompt: p,
        aspectRatio,
        resolution,
      });
      // Optimistic prepend into the gallery cache + background refresh for
      // any list-row fields the create endpoint didn't return.
      mutateGallery(ENDPOINT, (prev) => {
        if (prev.some((it) => it.id === res.id)) return prev;
        return [res as unknown as { id: string }, ...prev];
      });
      onCreated?.();
      setPrompt('');
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <>
      <button type="button" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button
        type="button"
        className="btn-primary"
        onClick={() => void submit()}
        disabled={busy || prompt.trim().length === 0}
      >
        {busy ? 'Generating…' : 'Generate'}
      </button>
    </>
  );

  return (
    <NewAssetModal
      open={open}
      onClose={onClose}
      busy={busy}
      title="New image"
      footer={footer}
    >
      <div className="stack">
        <label className="stack">
          <span className="muted small">Prompt</span>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={4}
            placeholder="A charcoal sketch of a cathedral at dusk…"
          />
        </label>
        <div className="row">
          <label className="stack" style={{ flex: 1 }}>
            <span className="muted small">Aspect ratio</span>
            <select
              value={aspectRatio}
              onChange={(e) => setAspectRatio(e.target.value)}
            >
              <option value="1:1">1:1 square</option>
              <option value="16:9">16:9 landscape</option>
              <option value="9:16">9:16 portrait</option>
              <option value="4:3">4:3</option>
              <option value="3:4">3:4</option>
            </select>
          </label>
          <label className="stack" style={{ flex: 1 }}>
            <span className="muted small">Resolution</span>
            <select
              value={resolution}
              onChange={(e) => setResolution(e.target.value)}
            >
              <option value="1K">1K</option>
              <option value="2K">2K</option>
            </select>
          </label>
        </div>
        {err ? <div className="status-missing small">{err}</div> : null}
      </div>
    </NewAssetModal>
  );
}
